// src/cockpit4/components/SentimentTrackerPanel.tsx
// ============================================
// 品牌监测 → 品牌舆情追踪（截图 4 + 截图 5）
//
// 截图 4 区域：
//   · 数据概览：提及品牌AI对话次数、好感度、正/中/负面情感、全部引用文章、有品牌引用文章、负面引用文章
//   · 平台舆情表现表：8 平台 × 网页/手机 × 8 指标（提及对话、好感度、正/中/负面、正负面词、有品牌文章、负面引用）
//
// 截图 5 区域：
//   · 舆情追踪趋势：情感倾向趋势、提及好感度趋势、对话提及正面词云、对话提及负面词云
//   · 负面舆情追踪：提及舆情词趋势、负面文章排行表格
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  MessageCircle, Plus, Calendar, ChevronDown, Info,
  TrendingUp, Cloud, AlertTriangle, BarChart3,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line,
} from "recharts";
import type { BrandMonitorData } from "../../runtime/GrowthFactory/brandMonitorData";
import {
  ALL_MONITOR_PLATFORMS,
} from "../../runtime/GrowthFactory/brandMonitorData";

interface Props {
  data: BrandMonitorData;
  brand: string;
  onAddBrand: () => void;
  platforms: string[];
  onPlatformsChange: (keys: string[]) => void;
  questions: { id: string; label: string }[];
  questionsFilter: string[];
  onQuestionsChange: (ids: string[]) => void;
  dateStart: string;
  dateEnd: string;
  onDateChange: (s: string, e: string) => void;
}

const SENTI_ROWS: readonly ({
  key: "mentionDialogs" | "mentionFavor" | "positiveRate" | "neutralRate" | "negativeRate" |
       "positiveWords" | "negativeWords" | "citeArticleBrand" | "citeArticleNegative";
  label: string;
  fmt: (v: number) => string;
  tip?: boolean;
})[] = [
  { key: "mentionDialogs", label: "提及对话次数", fmt: (v: number) => `${v}` },
  { key: "mentionFavor", label: "提及好感度", fmt: (v: number) => v < 0 ? "未提及" : `${Math.round(v)}`, tip: true },
  { key: "positiveRate", label: "正面情感", fmt: (v: number) => `${Math.round(v * 100)}%` },
  { key: "neutralRate", label: "中性情感", fmt: (v: number) => `${Math.round(v * 100)}%` },
  { key: "negativeRate", label: "负面情感", fmt: (v: number) => `${Math.round(v * 100)}%` },
  { key: "positiveWords", label: "对话提及正面词", fmt: (v: number) => `${v}` },
  { key: "negativeWords", label: "对话提及负面词", fmt: (v: number) => `${v}` },
  { key: "citeArticleBrand", label: "有品牌内容文章", fmt: (v: number) => `${v}` },
  { key: "citeArticleNegative", label: "负面引用文章", fmt: (v: number) => `${v}` },
];

/** 简易词云（文字气泡 + 字号按词频） */
function WordCloud({
  words, theme,
}: { words: Array<{ word: string; count: number }>; theme: "pos" | "neg" }) {
  if (words.length === 0) {
    return (
      <div className="text-[11px] text-gray-500 py-10 text-center">暂无数据</div>
    );
  }
  const maxC = Math.max(...words.map((w) => w.count));
  return (
    <div className="flex flex-wrap gap-2 p-3 items-center justify-center">
      {words.map((w) => {
        const size = 10 + Math.round((w.count / Math.max(1, maxC)) * 18);
        const opacity = 0.6 + (w.count / Math.max(1, maxC)) * 0.4;
        const color = theme === "pos" ? "#34D399" : "#F87171";
        return (
          <span key={w.word}
            style={{
              fontSize: `${size}px`,
              color,
              opacity,
              fontWeight: w.count / maxC >= 0.6 ? 700 : 500,
              letterSpacing: "0.02em",
            }}
          >
            {w.word}
          </span>
        );
      })}
    </div>
  );
}

export default function SentimentTrackerPanel(props: Props) {
  const {
    data, brand, onAddBrand,
    platforms, onPlatformsChange,
    questions, questionsFilter, onQuestionsChange,
    dateStart, dateEnd, onDateChange,
  } = props;
  const [pOpen, setPOpen] = useState(false);
  const [qOpen, setQOpen] = useState(false);
  const kpi = data.kpi;

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* 顶栏 */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MessageCircle size={15} className="text-violet-400" />
          <span className="text-[13px] font-semibold text-white">品牌舆情追踪</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onAddBrand}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px]
                       bg-white/[0.03] border border-white/[0.08] text-gray-400
                       hover:text-white hover:border-violet-500/40">
            <Plus size={12} /> 增加品牌监测
          </button>
          <div className="relative">
            <button onClick={() => { setPOpen((o) => !o); setQOpen(false); }}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] bg-black/30 border border-white/[0.08] text-white">
              {`全部${platforms.length}个平台`} <ChevronDown size={12} />
            </button>
            {pOpen && (
              <div className="absolute top-full mt-1 left-0 z-20 bg-[#0e1220] border border-white/[0.1] rounded-md p-2 w-48 shadow-2xl">
                {ALL_MONITOR_PLATFORMS.map((p) => {
                  const on = platforms.includes(p.key);
                  return (
                    <button key={p.key}
                      onClick={() => onPlatformsChange(on ? platforms.filter((x) => x !== p.key) : [...platforms, p.key])}
                      className={`block w-full text-left px-2 py-1 rounded text-[10px] ${on ? "text-white bg-white/10" : "text-gray-400"}`}>
                      <span className="w-2 h-2 inline-block rounded-full mr-1" style={{ background: p.color }} />
                      {p.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="relative">
            <button onClick={() => { setQOpen((o) => !o); setPOpen(false); }}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] bg-black/30 border border-white/[0.08] text-white">
              {`全部${questionsFilter.length}个问题`} <ChevronDown size={12} />
            </button>
            {qOpen && (
              <div className="absolute top-full mt-1 left-0 z-20 bg-[#0e1220] border border-white/[0.1] rounded-md p-2 w-80 shadow-2xl max-h-60 overflow-auto">
                {questions.map((q) => {
                  const on = questionsFilter.includes(q.id);
                  return (
                    <button key={q.id}
                      onClick={() => onQuestionsChange(on ? questionsFilter.filter((x) => x !== q.id) : [...questionsFilter, q.id])}
                      className={`block w-full text-left px-2 py-1 rounded text-[10px] ${on ? "text-white bg-white/10" : "text-gray-400"}`}>
                      ▸ {q.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] bg-black/30 border border-white/[0.08] text-gray-300">
            <Calendar size={12} />
            <input type="date" value={dateStart} onChange={(e) => onDateChange(e.target.value, dateEnd)}
              className="bg-transparent outline-none w-[88px] text-white" />
            <span className="text-gray-500">至</span>
            <input type="date" value={dateEnd} onChange={(e) => onDateChange(dateStart, e.target.value)}
              className="bg-transparent outline-none w-[88px] text-white" />
          </div>
        </div>
      </div>

      {/* 截图4：数据概览 */}
      <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-1 h-3 rounded bg-violet-500" />
          <span className="text-[11px] font-semibold text-white">数据概览</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="grid grid-cols-5 gap-2">
            <div className="rounded-md bg-white/[0.02] border border-white/[0.05] p-2.5 col-span-1">
              <div className="text-[20px] font-bold text-cyan-400 tracking-tight">
                {kpi.mentionDialogs}
              </div>
              <div className="text-[10px] text-gray-300 mt-1">提及品牌AI对话次数</div>
            </div>
            <div className="col-span-4 rounded-md bg-white/[0.02] border border-white/[0.05] p-2.5">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] text-gray-300 flex items-center gap-1">
                  品牌提及好感度 <Info size={9} className="text-gray-600" />
                </div>
                <span className="text-[16px] font-bold text-violet-400">
                  {kpi.mentionFavor < 0 ? "--" : `${kpi.mentionFavor}`}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div className="rounded bg-emerald-500/[0.08] border border-emerald-500/20 p-2">
                  <div className="text-[14px] font-bold text-emerald-400">{Math.round(kpi.sentimentPositive * 100)}%</div>
                  <div className="text-[9px] text-gray-400">正面情感</div>
                </div>
                <div className="rounded bg-cyan-500/[0.08] border border-cyan-500/20 p-2">
                  <div className="text-[14px] font-bold text-cyan-400">{Math.round(kpi.sentimentNeutral * 100)}%</div>
                  <div className="text-[9px] text-gray-400">中性情感</div>
                </div>
                <div className="rounded bg-red-500/[0.08] border border-red-500/20 p-2">
                  <div className="text-[14px] font-bold text-red-400">{Math.round(kpi.sentimentNegative * 100)}%</div>
                  <div className="text-[9px] text-gray-400">负面情感</div>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: "全部引用文章", v: kpi.articlesTotal, color: "text-cyan-400" },
              { label: "有品牌内容引用文章", v: kpi.articlesWithBrand, color: "text-emerald-400" },
              { label: "负面引用文章", v: kpi.articlesNegative, color: "text-red-400" },
            ].map((i) => (
              <div key={i.label} className="rounded-md bg-white/[0.02] border border-white/[0.05] p-2.5">
                <div className={`text-[20px] font-bold tracking-tight ${i.color}`}>{i.v}</div>
                <div className="text-[10px] text-gray-300 mt-1">{i.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 平台舆情表现表（截图4） */}
      <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-1 h-4 rounded bg-violet-500" />
          <span className="text-[12px] font-semibold text-white">平台舆情表现</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[10.5px] border-collapse">
            <thead>
              <tr className="border-b border-white/[0.06] bg-black/30">
                <th className="text-left text-gray-400 font-mono font-normal p-2 min-w-[130px] sticky left-0 bg-[#0c1020]">
                  <div className="flex flex-col items-start">
                    <span className="text-gray-400 mb-1">指标</span>
                    <span className="text-gray-500">平台</span>
                  </div>
                </th>
                {ALL_MONITOR_PLATFORMS.map((p) => (
                  <th key={p.key} colSpan={p.endpoints.length} className="p-2 min-w-[120px]">
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                        style={{ background: p.color }}>{p.short}</span>
                      <span className="text-white">{p.name}</span>
                      {!p.enabled && <span className="text-[8px] px-1 rounded bg-white/[0.05] text-gray-500">接入中</span>}
                    </div>
                    <div className="flex gap-2 justify-center">
                      {p.endpoints.map((e) => (
                        <span key={e} className="text-[9px] font-mono text-gray-500">
                          {e === "web" ? "网页" : "手机"}
                        </span>
                      ))}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SENTI_ROWS.map((row, idx) => (
                <tr key={row.key} className={idx % 2 ? "bg-white/[0.01]" : ""}>
                  <td className="p-2 text-gray-300 font-medium sticky left-0 bg-[#0c1020] border-r border-white/[0.04]">
                    {row.label}
                    {row.tip && <Info size={9} className="text-gray-600 inline ml-1" />}
                  </td>
                  {ALL_MONITOR_PLATFORMS.map((p) =>
                    p.endpoints.map((end) => {
                      const metric = data.platformMetrics[p.key]?.[end];
                      if (!metric) return <td key={`${p.key}-${end}`} className="p-2 text-center text-gray-600">--</td>;
                      const raw = (metric as any)[row.key];
                      const text = row.fmt(raw);
                      return (
                        <td key={`${p.key}-${end}`} className="p-2 text-center text-gray-300">{text}</td>
                      );
                    })
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 截图 5 舆情追踪趋势：情感+好感度+词云 */}
      <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-1 h-4 rounded bg-violet-500" />
          <span className="text-[12px] font-semibold text-white">舆情追踪趋势</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="rounded-md border border-white/[0.05] p-2.5">
            <div className="text-[11px] text-gray-300 mb-1.5">情感倾向趋势</div>
            {data.sentimentTrend.length === 0 ? (
              <div className="py-14 text-center text-[11px] text-gray-500">数据较少，暂时无法分析</div>
            ) : (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.sentimentTrend} margin={{ left: 5, right: 20, top: 5, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                    <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                    <Tooltip contentStyle={{ background: "#0e1220", border: "1px solid rgba(255,255,255,0.08)", fontSize: 11 }}
                      formatter={(v: any) => `${Math.round((Number(v) || 0) * 100)}%`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="pos" name="正面" stackId="1" stroke="#10B981" fill="#10B981" />
                    <Area type="monotone" dataKey="neu" name="中性" stackId="1" stroke="#60A5FA" fill="#60A5FA" />
                    <Area type="monotone" dataKey="neg" name="负面" stackId="1" stroke="#F87171" fill="#F87171" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="rounded-md border border-white/[0.05] p-2.5">
            <div className="text-[11px] text-gray-300 mb-1.5">提及好感度趋势</div>
            {data.favorTrend.length === 0 ? (
              <div className="py-14 text-center text-[11px] text-gray-500">暂无数据</div>
            ) : (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.favorTrend} margin={{ left: 5, right: 20, top: 5, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                    <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" domain={[0, 100]} />
                    <Tooltip contentStyle={{ background: "#0e1220", border: "1px solid rgba(255,255,255,0.08)", fontSize: 11 }} />
                    <Line type="monotone" dataKey="favor" name="好感度 /100" stroke="#22D3EE" strokeWidth={2.2} dot={{ r: 3, fill: "#22D3EE" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="rounded-md border border-white/[0.05] p-2.5">
            <div className="text-[11px] text-gray-300 mb-1.5">对话提及正面词云</div>
            <WordCloud words={data.positiveCloud} theme="pos" />
          </div>
          <div className="rounded-md border border-white/[0.05] p-2.5">
            <div className="text-[11px] text-gray-300 mb-1.5">对话提及负面词云</div>
            <WordCloud words={data.negativeCloud} theme="neg" />
          </div>
        </div>
      </div>

      {/* 截图5 底部：负面舆情追踪 */}
      <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-1 h-4 rounded bg-red-500/70" />
          <span className="text-[12px] font-semibold text-white">负面舆情追踪</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="rounded-md border border-white/[0.05] p-2.5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] text-gray-300 flex items-center gap-1">
                <AlertTriangle size={12} className="text-red-400" /> 提及舆情词趋势
              </span>
              <select className="text-[10px] bg-black/30 border border-white/[0.08] text-gray-300 rounded px-2 py-1 outline-none"
                defaultValue="count">
                <option value="count">提及次数</option>
              </select>
            </div>
            {data.sentimentKeywordTrend.length === 0 ? (
              <div className="py-14 text-center text-[11px] text-gray-500">暂无数据</div>
            ) : (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.sentimentKeywordTrend} margin={{ left: 5, right: 20, top: 5, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                    <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                    <Tooltip contentStyle={{ background: "#0e1220", border: "1px solid rgba(255,255,255,0.08)", fontSize: 11 }} />
                    <Bar dataKey="count" fill="#F87171" name="提及舆情词次数" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="rounded-md border border-white/[0.05] p-2.5 overflow-x-auto">
            <div className="text-[11px] text-gray-300 mb-2 flex items-center gap-1">
              <BarChart3 size={12} className="text-violet-400" /> 负面文章排行
            </div>
            <table className="min-w-full text-[10.5px] border-collapse">
              <thead className="bg-black/30 text-gray-400 font-mono">
                <tr>
                  <th className="text-left p-2 min-w-[180px]">文章名称</th>
                  <th className="text-left p-2">发布时间</th>
                  <th className="text-left p-2">发布站点</th>
                  <th className="text-left p-2">文章负面词 <Info size={9} className="text-gray-600 inline" /></th>
                  <th className="text-left p-2">引用AI平台</th>
                  <th className="text-left p-2">引用AI问题</th>
                  <th className="text-right p-2">引用次数</th>
                </tr>
              </thead>
              <tbody>
                {data.negativeArticles.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-[11px] text-gray-500 py-6">
                      暂无负面文章：完成至少 1 次监测任务后自动刷新
                    </td>
                  </tr>
                ) : (
                  data.negativeArticles.map((a, i) => (
                    <tr key={i} className={i % 2 ? "bg-white/[0.01]" : ""}>
                      <td className="p-2 text-white font-mono max-w-[200px] truncate">{a.title}</td>
                      <td className="p-2 text-gray-400">{a.publishedAt}</td>
                      <td className="p-2 text-cyan-400">{a.site}</td>
                      <td className="p-2"><span
                        className={`px-2 py-0.5 rounded ${a.negativeScore > 70 ? "bg-red-500/10 text-red-400 border border-red-500/20" : a.negativeScore > 30 ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" : "bg-white/[0.03] text-gray-400 border border-white/[0.05]"}`}
                      >{a.negativeScore}</span></td>
                      <td className="p-2 text-violet-400">{a.citeAIs.join("/") || "-"}</td>
                      <td className="p-2 text-emerald-400 max-w-[120px] truncate">{a.citeQuestions.join("/") || "-"}</td>
                      <td className="p-2 text-right text-pink-400 font-semibold">{a.citeCount}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
